import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

// Lib
import { INSTAGRAM, TWITTER } from '@/lib/socialProfile';
import pageRights from '@/lib/acl/pageRights';
import { GROUP_HOMEPAGE, HOMEPAGE, SCHEDULER } from '@/lib/routes/privateRoutes';
import { TEAM_MEMBER_ROLE } from '@/lib/user/roles';
import { URL_TWITTER_AFTER_MIGRATION_ALLOWED } from '@/lib/socialprofiles/twitterRestrictions';

// Hooks
import useAcl from '@/hooks/user/useAcl';
import useUser from '@/utils/hooks/swr/useUser';
import useActiveGroup from '@/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';
import useTwitterProfile from '@/hooks/socialprofiles/useTwitterProfile';
import useUrlUserHomepage from '@/utils/hooks/user/useUrlUserHomepage';

// Components
import LoaderPage from '@/components/base/Loader/LoaderPage';

const useHasAccessToPage = () => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const isGroup = useIsGroup();
    const { hasAcl } = useAcl();
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const network = activeSocialProfile?.social_network_type ?? INSTAGRAM;

    const hasAccessToPage = (urlWithoutParams) => {
        /**
         * FOR ACTIVE SOCIAL PROFILES
         */
        if (activeSocialProfile) {
            if (pageRights?.[urlWithoutParams]?.networks) {
                if (!pageRights?.[urlWithoutParams]?.networks?.includes(network)) {
                    return false;
                }
            }

            // check if page has an acl applied to a specific network
            if (pageRights?.[urlWithoutParams]?.acls?.[network]) {
                // if acl check if the user has the rights
                if (
                    pageRights?.[urlWithoutParams]?.acls?.[
                        activeSocialProfile?.social_network_type
                    ]?.filter((acl) => hasAcl(acl))?.length !==
                    pageRights?.[urlWithoutParams]?.acls?.[network]?.length
                ) {
                    return false;
                }
            }
        }

        /**
         * FOR ACTIVE GROUP
         */
        if (isGroup) {
            if ('group' in pageRights[urlWithoutParams]) {
                if (!pageRights[urlWithoutParams].group) {
                    return false;
                }
            }
        }

        /**
         * USER CHECK
         */
        if (pageRights?.[urlWithoutParams]?.roles) {
            if (
                !pageRights?.[urlWithoutParams]?.roles?.includes(user?.acl_role ?? TEAM_MEMBER_ROLE)
            ) {
                return false;
            }
        }

        if (pageRights?.[urlWithoutParams]?.permissions) {
            if (
                pageRights?.[urlWithoutParams]?.permissions?.filter((permission) =>
                    user?.permissions?.includes(permission)
                )?.length !== pageRights?.[urlWithoutParams]?.permissions?.length
            ) {
                return false;
            }
        }

        return true;
    };

    return { hasAccessToPage };
};

export const usePageAccessRights = () => {
    const { pathname } = useRouter();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const { isTwitterProfileCreatedAfterMigration } = useTwitterProfile();
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { hasAccessToPage } = useHasAccessToPage();
    const { userHomepage } = useUrlUserHomepage();

    const network = activeSocialProfile?.social_network_type ?? INSTAGRAM;
    const urlWithoutParams = pathname.split('?')[0];

    const pageToBeChecked =
        urlWithoutParams in pageRights ||
        (activeSocialProfile &&
            network === TWITTER &&
            !URL_TWITTER_AFTER_MIGRATION_ALLOWED.includes(urlWithoutParams));

    const [stateAccessRights, setStateAccessRights] = useState({
        isLoading: pageToBeChecked,
        hasAccess: !pageToBeChecked,
        newHomePage: HOMEPAGE
    });

    useEffect(() => {
        if (
            activeSocialProfile &&
            network === TWITTER &&
            !URL_TWITTER_AFTER_MIGRATION_ALLOWED.includes(urlWithoutParams)
        ) {
            const createdAt = activeSocialProfile.created_at;
            const isCreatedAfterMigration = isTwitterProfileCreatedAfterMigration(createdAt);
            if (isCreatedAfterMigration) {
                setStateAccessRights({
                    isLoading: false,
                    hasAccess: false,
                    newHomePage: SCHEDULER
                });
                return undefined;
            }
        }

        let userHomepageUrl = HOMEPAGE;
        if (typeof activeGroup === 'object' && activeSocialProfile === null) {
            setStateAccessRights((prevState) => ({
                ...prevState,
                newHomePage: GROUP_HOMEPAGE
            }));
        } else if (userHomepage in pageRights) {
            if (hasAccessToPage(userHomepage)) {
                userHomepageUrl = userHomepage;
            }

            setStateAccessRights((prevState) => ({
                ...prevState,
                newHomePage: userHomepageUrl
            }));
        }

        if (pageToBeChecked) {
            const hasAccessToPageResult = hasAccessToPage(urlWithoutParams);

            setStateAccessRights((prevState) => ({
                ...prevState,
                isLoading: false,
                hasAccess: hasAccessToPageResult
            }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeSocialProfile?.id,
        activeSocialProfile?.social_network_type,
        user?.acl_role,
        pageToBeChecked,
        urlWithoutParams,
        user?.permissions,
        userHomepage
    ]);

    return {
        hasAccess: stateAccessRights.hasAccess,
        isLoading: stateAccessRights.isLoading,
        newHomePage: stateAccessRights?.newHomePage
    };
};

const PageAccessRightsMiddleware = ({ children }) => {
    const router = useRouter();
    const { hasAccess, isLoading, newHomePage } = usePageAccessRights();

    router.prefetch(newHomePage);

    if (!hasAccess && !isLoading) {
        router.push(newHomePage);
        return <LoaderPage />;
    }

    if (isLoading) {
        return <LoaderPage />;
    }

    return children;
};

export default PageAccessRightsMiddleware;
